import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import useClassNames from 'helpers/hooks/useClassNames';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop } from 'helpers/utils/screensizes';
import Typography from '../typography';

export interface CheckboxProps extends Omit<React.ComponentProps<'input'>, 'onChange' | 'key'> {
  label?: string;
  labelPosition?: 'on-left' | 'on-right';
  containerClassName?: string;
  onChange?: (props: { name: string; checked: boolean }) => void;
  disableBackground?: boolean;
}

const Checkbox: React.FC<CheckboxProps> = ({
  className = '',
  checked,
  onChange,
  onMouseOver,
  onMouseLeave,
  containerClassName,
  label,
  defaultChecked = false,
  labelPosition = 'on-right',
  disableBackground = false,
  ...props
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isChecked, setIsChecked] = useState(checked ?? defaultChecked ?? false);

  const [isDesktopSize] = useMediaQuery(desktop);

  const checkboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (typeof checked !== 'undefined') setIsChecked(checked);
  }, [checked]);

  const handleContainerClick = () => {
    if (!isDesktopSize) {
      toggleIsChecked();
    }
  };

  const toggleIsChecked = useCallback(() => {
    onChange?.({ name: props.name ?? '', checked: !isChecked });
    setIsChecked(!isChecked);
  }, [isChecked, onChange, props.name]);

  const handleMouseOver = (e: React.MouseEvent<HTMLInputElement>) => {
    setIsHovered(isDesktopSize);
    onMouseOver?.(e);
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLInputElement>) => {
    setIsHovered(false);
    onMouseLeave?.(e);
  };

  const containerClassNames = useClassNames(['flex items-center gap-12', containerClassName]);
  const buttonClassName = useClassNames([
    'h-20 w-20 2xl:h-24 2xl:w-24 min-w-[20px] relative outline outline-2 cursor-pointer',
    isHovered ? 'outline-secondary-black' : 'outline-[#090909]',
  ]);

  const inputClassName = useClassNames([
    'absolute w-full h-full z-[2] checked:bg-none bg-transparent text-transparent border-transparent',
    className,
  ]);

  const iconClassName = useClassNames([
    'absolute top-[50%] z-[1] left-[50%] w-12 h-12 2xl:h-16 2xl:w-16 translate-y-[-50%] translate-x-[-50%] stroke-[2px]',
    isChecked || isHovered ? 'block' : 'hidden',
    isChecked && !disableBackground ? 'text-white' : 'text-secondary-black',
  ]);

  const backgroundClassName = useClassNames([
    'absolute z-0 h-20 w-20 2xl:h-24 2xl:w-24',
    { 'bg-secondary-black': !!isChecked },
  ]);

  const LabelElement = (
    <Typography as="label" className="text-14 text-secondary-black">
      {label}
    </Typography>
  );

  return (
    <div className={containerClassNames} onClick={handleContainerClick}>
      {label && labelPosition === 'on-left' && LabelElement}

      <div className={buttonClassName}>
        <input
          ref={checkboxRef}
          type="checkbox"
          checked={isChecked}
          className={inputClassName}
          onMouseOver={handleMouseOver}
          onMouseLeave={handleMouseLeave}
          onChange={toggleIsChecked}
          {...props}
        />

        <CheckIcon className={iconClassName} />

        {!disableBackground && <div className={backgroundClassName} />}
      </div>

      {label && labelPosition === 'on-right' && LabelElement}
    </div>
  );
};

export default Checkbox;
